import React, { useState, useEffect, useCallback } from 'react';
import { useWeb3 } from '../context/Web3Context';
import { useWebSocket } from '../context/WebSocketContext';
import { usePushNotifications } from '../hooks/usePushNotifications';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetTrigger,
} from './ui/sheet';
import { ScrollArea } from './ui/scroll-area';
import { 
    Bell, 
    Trophy, 
    XCircle, 
    CheckCircle, 
    Scale, 
    AlertTriangle,
    ExternalLink,
    Loader2,
    BellRing,
    BellOff
} from 'lucide-react';
import { Link } from 'react-router-dom';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const notificationIcons = {
    bet_accepted: { icon: CheckCircle, color: 'text-[#00FF66]', bg: 'bg-[#00FF66]/10' },
    bet_won: { icon: Trophy, color: 'text-[#FFD700]', bg: 'bg-[#FFD700]/10' },
    bet_lost: { icon: XCircle, color: 'text-red-400', bg: 'bg-red-400/10' },
    oracle_assertion: { icon: Scale, color: 'text-[#9B6FE8]', bg: 'bg-[#6F3FCA]/10' },
    oracle_dispute: { icon: AlertTriangle, color: 'text-orange-400', bg: 'bg-orange-400/10' },
};

export function NotificationCenter() {
    const { address, isConnected } = useWeb3();
    const { lastMessage } = useWebSocket();
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [loading, setLoading] = useState(false);
    const [open, setOpen] = useState(false);
    
    // Push notifications
    const { 
        isSupported: pushSupported, 
        isGranted: pushGranted, 
        requestPermission,
        notifyBetAccepted,
        notifyBetWon,
        notifyBetLost,
        notifyOracleAssertion,
        notifyOracleDispute
    } = usePushNotifications();

    const fetchNotifications = useCallback(async () => {
        if (!address) return;
        
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}/api/notifications/${address}?limit=30`);
            if (response.ok) {
                const data = await response.json();
                setNotifications(data);
            }
        } catch (error) {
            console.error('Failed to fetch notifications:', error);
        } finally {
            setLoading(false);
        }
    }, [address]);

    const fetchUnreadCount = useCallback(async () => {
        if (!address) return;
        
        try {
            const response = await fetch(`${API_URL}/api/notifications/${address}/unread-count`);
            if (response.ok) {
                const data = await response.json();
                setUnreadCount(data.count);
            }
        } catch (error) {
            console.error('Failed to fetch unread count:', error);
        }
    }, [address]);

    const markAllAsRead = async () => {
        if (!address) return;
        
        try {
            await fetch(`${API_URL}/api/notifications/${address}/mark-read`, {
                method: 'POST'
            });
            setUnreadCount(0);
            setNotifications(prev => prev.map(n => ({ ...n, read: true })));
        } catch (error) {
            console.error('Failed to mark as read:', error);
        }
    };

    // Fetch notifications when panel opens
    useEffect(() => {
        if (open && address) {
            fetchNotifications();
        }
    }, [open, address, fetchNotifications]);

    // Poll for unread count
    useEffect(() => {
        if (!address) {
            setUnreadCount(0);
            return;
        }
        
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 30000); // Every 30 seconds
        
        return () => clearInterval(interval);
    }, [address, fetchUnreadCount]);

    // Handle real-time notifications from shared WebSocket context
    useEffect(() => {
        if (!lastMessage || lastMessage.type !== 'notification') return;
        
        const data = lastMessage;
        // New notification received
        setUnreadCount(prev => prev + 1);
        setNotifications(prev => [data, ...prev].slice(0, 30));
        
        // Send push notification if granted
        if (pushGranted) {
            const notifType = data.notification_type;
            if (notifType === 'bet_accepted') {
                notifyBetAccepted(data.message || '');
            } else if (notifType === 'bet_won') {
                notifyBetWon(data.message || '', data.metadata?.payout || 0);
            } else if (notifType === 'bet_lost') {
                notifyBetLost(data.message || '');
            } else if (notifType === 'oracle_assertion') {
                notifyOracleAssertion(data.message || '', data.metadata?.creator_wins_asserted ? 'Creator' : 'Taker');
            } else if (notifType === 'oracle_dispute') {
                notifyOracleDispute(data.message || '');
            }
        }
    }, [lastMessage, pushGranted, notifyBetAccepted, notifyBetWon, notifyBetLost, notifyOracleAssertion, notifyOracleDispute]);

    const formatTime = (timestamp) => {
        if (!timestamp) return '';
        const date = new Date(timestamp);
        const now = new Date();
        const diff = now - date;
        
        if (diff < 60000) return 'Just now';
        if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
        if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
        return date.toLocaleDateString();
    };

    if (!isConnected) return null;

    return (
        <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
                <Button 
                    variant="ghost" 
                    size="icon" 
                    className="relative text-white/70 hover:text-white hover:bg-white/5"
                    data-testid="notification-bell"
                >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#00FF66] text-[#0A0A0C] text-xs font-bold rounded-full flex items-center justify-center">
                            {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                    )}
                </Button>
            </SheetTrigger>
            <SheetContent className="bg-[#0A0A0C] border-white/10 w-full sm:w-[400px]">
                <SheetHeader className="border-b border-white/10 pb-4">
                    <div className="flex items-center justify-between">
                        <SheetTitle className="text-white flex items-center gap-2">
                            <Bell className="w-5 h-5 text-[#00FF66]" />
                            Notifications
                        </SheetTitle>
                        <div className="flex items-center gap-2">
                            {/* Push notification toggle */}
                            {pushSupported && (
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    className={`text-xs ${pushGranted ? 'text-[#00FF66]' : 'text-white/50'}`}
                                    onClick={requestPermission}
                                    title={pushGranted ? 'Push notifications enabled' : 'Enable push notifications'}
                                >
                                    {pushGranted ? (
                                        <BellRing className="w-4 h-4" />
                                    ) : (
                                        <BellOff className="w-4 h-4" />
                                    )}
                                </Button>
                            )}
                            {unreadCount > 0 && (
                                <Button 
                                    variant="ghost" 
                                    size="sm" 
                                    className="text-[#00FF66] hover:text-[#00CC52] text-xs"
                                    onClick={markAllAsRead}
                                >
                                    Mark all read
                                </Button>
                            )}
                        </div>
                    </div>
                    {/* Push notification status */}
                    {pushSupported && !pushGranted && (
                        <div className="mt-2 p-2 rounded bg-white/5 text-xs text-white/50 flex items-center gap-2">
                            <BellOff className="w-3 h-3" />
                            <span>Enable push notifications for alerts</span>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="ml-auto text-[#00FF66] text-xs py-0 h-auto"
                                onClick={requestPermission}
                            >
                                Enable
                            </Button>
                        </div>
                    )}
                </SheetHeader>
                
                <ScrollArea className="h-[calc(100vh-120px)] mt-4">
                    {loading ? (
                        <div className="flex items-center justify-center py-8">
                            <Loader2 className="w-6 h-6 animate-spin text-[#00FF66]" />
                        </div>
                    ) : notifications.length === 0 ? (
                        <div className="text-center py-8">
                            <Bell className="w-12 h-12 text-white/20 mx-auto mb-3" />
                            <p className="text-white/40">No notifications yet</p>
                            <p className="text-white/30 text-sm mt-1">
                                You'll be notified when your bets are accepted or resolved
                            </p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {notifications.map((notification, index) => {
                                const config = notificationIcons[notification.notification_type || notification.type] || 
                                    { icon: Bell, color: 'text-white/60', bg: 'bg-white/5' };
                                const Icon = config.icon;
                                
                                return (
                                    <div 
                                        key={index}
                                        className={`p-3 rounded-lg border transition-colors ${
                                            notification.read 
                                                ? 'bg-white/[0.02] border-white/5' 
                                                : 'bg-white/[0.04] border-[#00FF66]/20'
                                        }`}
                                    >
                                        <div className="flex gap-3">
                                            <div className={`w-10 h-10 rounded-full ${config.bg} flex items-center justify-center flex-shrink-0`}>
                                                <Icon className={`w-5 h-5 ${config.color}`} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-start justify-between gap-2">
                                                    <p className="text-sm font-medium text-white">
                                                        {notification.title}
                                                    </p>
                                                    {!notification.read && (
                                                        <div className="w-2 h-2 rounded-full bg-[#00FF66] flex-shrink-0 mt-1.5" />
                                                    )}
                                                </div>
                                                <p className="text-xs text-white/50 mt-0.5 line-clamp-2">
                                                    {notification.message}
                                                </p>
                                                <div className="flex items-center justify-between mt-2">
                                                    <span className="text-xs text-white/30">
                                                        {formatTime(notification.created_at || notification.timestamp)}
                                                    </span>
                                                    {notification.bet_id && (
                                                        <Link 
                                                            to={`/bets/${notification.bet_id}`}
                                                            className="text-xs text-[#00FF66] hover:underline flex items-center gap-1"
                                                            onClick={() => setOpen(false)}
                                                        >
                                                            View bet
                                                            <ExternalLink className="w-3 h-3" />
                                                        </Link>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </ScrollArea>
            </SheetContent>
        </Sheet>
    );
}
